/**
 * Big Data Cloud Service
 * Handles storage and retrieval of user interaction data
 */

class BigDataCloudService {
  constructor() {
    this.baseURL = 'http://localhost:5000/api/ai';
    this.storage = {
      userInteractions: [],
      searchData: [],
      clickTracking: [],
      analytics: {}
    };
  }

  /**
   * Store user interaction data
   */
  async storeUserInteraction(interactionData) {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(`${this.baseURL}/big-data-cloud`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          query: interactionData.eventType,
          results: interactionData,
          metadata: {
            userId: interactionData.userId,
            sessionId: interactionData.sessionId,
            eventType: interactionData.eventType,
            timestamp: interactionData.timestamp,
            page: interactionData.page
          }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Store locally as backup
      this.storage.userInteractions.push({
        ...interactionData,
        storageId: result.storageId,
        storedAt: new Date().toISOString()
      });

      console.log('User interaction stored:', result);
      return result;
    } catch (error) {
      console.error('Error storing user interaction:', error);
      // Store locally as fallback
      this.storeLocally('userInteractions', interactionData);
      throw error;
    }
  }

  /**
   * Store search data
   */
  async storeSearchData(searchData) {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(`${this.baseURL}/big-data-cloud`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          query: searchData.query,
          results: searchData,
          metadata: {
            userId: searchData.userId,
            userRole: searchData.userRole,
            searchSource: searchData.searchSource,
            eventType: 'search_initiated',
            estimatedResults: searchData.estimatedResults,
            relatedTopics: searchData.relatedTopics,
            userInterests: searchData.userInterests
          }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Store locally as backup
      this.storage.searchData.push({
        ...searchData,
        storageId: result.storageId,
        storedAt: new Date().toISOString()
      });

      console.log('Search data stored:', result);
      return result;
    } catch (error) {
      console.error('Error storing search data:', error);
      // Store locally as fallback
      this.storeLocally('searchData', searchData);
      throw error;
    }
  }

  /**
   * Store click tracking data
   */
  async storeClickData(clickData) {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(`${this.baseURL}/track-click`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(clickData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Store locally as backup
      this.storage.clickTracking.push({
        ...clickData,
        storageId: result.storageId,
        storedAt: new Date().toISOString()
      });

      console.log('Click data stored:', result);
      return result;
    } catch (error) {
      console.error('Error storing click data:', error);
      // Store locally as fallback
      this.storeLocally('clickTracking', clickData);
      throw error;
    }
  }

  /**
   * Store data locally as fallback
   */
  storeLocally(storageType, data) {
    try {
      const storageKey = `bigDataCloud_${storageType}`;
      const storedData = JSON.parse(localStorage.getItem(storageKey) || '[]');

      storedData.push({
        ...data,
        storedLocally: true,
        storedAt: new Date().toISOString()
      });

      // Keep only last 500 items to prevent localStorage bloat
      if (storedData.length > 500) {
        storedData.splice(0, storedData.length - 500);
      }

      localStorage.setItem(storageKey, JSON.stringify(storedData));
      console.log(`Data stored locally in ${storageKey}`);
    } catch (error) {
      console.error('Error storing data locally:', error);
    }
  }

  /**
   * Get user analytics data
   */
  async getUserAnalytics(userId, dateRange = null) {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token available');
      }

      const params = new URLSearchParams();
      if (userId) params.append('userId', userId);
      if (dateRange) params.append('dateRange', dateRange);

      const response = await fetch(`${this.baseURL}/user-analytics?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const analytics = await response.json();
      return analytics;
    } catch (error) {
      console.error('Error fetching user analytics:', error);
      // Return local analytics as fallback
      return this.getLocalAnalytics(userId);
    }
  }

  /**
   * Get local analytics data
   */
  getLocalAnalytics(userId) {
    const userInteractions = JSON.parse(localStorage.getItem('bigDataCloud_userInteractions') || '[]');
    const searchData = JSON.parse(localStorage.getItem('bigDataCloud_searchData') || '[]');
    const clickTracking = JSON.parse(localStorage.getItem('bigDataCloud_clickTracking') || '[]');

    // Filter by userId if provided
    const filterByUser = (data) => userId ? data.filter(item => item.userId === userId) : data;

    const filteredInteractions = filterByUser(userInteractions);
    const filteredSearches = filterByUser(searchData);
    const filteredClicks = filterByUser(clickTracking);

    return {
      totalInteractions: filteredInteractions.length,
      totalSearches: filteredSearches.length,
      totalClicks: filteredClicks.length,
      eventTypes: this.categorizeEvents(filteredInteractions),
      searchTrends: this.analyzeSearchTrends(filteredSearches),
      clickPatterns: this.analyzeClickPatterns(filteredClicks),
      timeBasedActivity: this.analyzeTimeBasedActivity(filteredInteractions),
      localData: true,
      lastUpdated: new Date().toISOString()
    };
  }

  /**
   * Categorize events by type
   */
  categorizeEvents(interactions) {
    const categories = {};

    interactions.forEach(interaction => {
      const type = interaction.eventType || 'unknown';
      categories[type] = (categories[type] || 0) + 1;
    });

    return categories;
  }

  /**
   * Analyze search trends
   */
  analyzeSearchTrends(searches) {
    const trends = {
      topQueries: {},
      searchSources: {},
      timeDistribution: {}
    };

    searches.forEach(search => {
      // Count query frequency
      const query = search.query || 'unknown';
      trends.topQueries[query] = (trends.topQueries[query] || 0) + 1;

      // Count search sources
      const source = search.searchSource || 'unknown';
      trends.searchSources[source] = (trends.searchSources[source] || 0) + 1;

      // Analyze time distribution
      const hour = new Date(search.timestamp).getHours();
      trends.timeDistribution[hour] = (trends.timeDistribution[hour] || 0) + 1;
    });

    return trends;
  }

  /**
   * Analyze click patterns
   */
  analyzeClickPatterns(clicks) {
    const patterns = {
      elementTypes: {},
      pageDistribution: {},
      timeDistribution: {}
    };

    clicks.forEach(click => {
      // Count element types
      const elementType = click.element?.tagName || 'unknown';
      patterns.elementTypes[elementType] = (patterns.elementTypes[elementType] || 0) + 1;

      // Count page distribution
      const page = click.page?.url || 'unknown';
      patterns.pageDistribution[page] = (patterns.pageDistribution[page] || 0) + 1;

      // Analyze time distribution
      const hour = new Date(click.timestamp).getHours();
      patterns.timeDistribution[hour] = (patterns.timeDistribution[hour] || 0) + 1;
    });

    return patterns;
  }

  /**
   * Analyze time-based activity
   */
  analyzeTimeBasedActivity(interactions) {
    const activity = {
      hourly: Array(24).fill(0),
      daily: {},
      weekly: {}
    };

    interactions.forEach(interaction => {
      const date = new Date(interaction.timestamp);
      const hour = date.getHours();
      const day = date.toDateString();
      const week = this.getWeekNumber(date);

      activity.hourly[hour]++;
      activity.daily[day] = (activity.daily[day] || 0) + 1;
      activity.weekly[week] = (activity.weekly[week] || 0) + 1;
    });

    return activity;
  }

  /**
   * Get week number of the year
   */
  getWeekNumber(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  }

  /**
   * Sync local data with backend
   */
  async syncWithBackend() {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const localData = {
        userInteractions: JSON.parse(localStorage.getItem('bigDataCloud_userInteractions') || '[]'),
        searchData: JSON.parse(localStorage.getItem('bigDataCloud_searchData') || '[]'),
        clickTracking: JSON.parse(localStorage.getItem('bigDataCloud_clickTracking') || '[]')
      };

      const response = await fetch(`${this.baseURL}/sync-local-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(localData)
      });

      if (response.ok) {
        // Clear local data after successful sync
        localStorage.removeItem('bigDataCloud_userInteractions');
        localStorage.removeItem('bigDataCloud_searchData');
        localStorage.removeItem('bigDataCloud_clickTracking');
        console.log('Local data synced with backend successfully');
      }
    } catch (error) {
      console.error('Error syncing with backend:', error);
    }
  }

  /**
   * Export all data
   */
  exportAllData() {
    return {
      storage: this.storage,
      localStorage: {
        userInteractions: JSON.parse(localStorage.getItem('bigDataCloud_userInteractions') || '[]'),
        searchData: JSON.parse(localStorage.getItem('bigDataCloud_searchData') || '[]'),
        clickTracking: JSON.parse(localStorage.getItem('bigDataCloud_clickTracking') || '[]'),
        userEvents: JSON.parse(localStorage.getItem('userEvents') || '[]'),
        failedEvents: JSON.parse(localStorage.getItem('failedEvents') || '[]')
      },
      exportTimestamp: new Date().toISOString(),
      version: '1.0.0'
    };
  }

  /**
   * Clear all data
   */
  clearAllData() {
    this.storage = {
      userInteractions: [],
      searchData: [],
      clickTracking: [],
      analytics: {}
    };

    localStorage.removeItem('bigDataCloud_userInteractions');
    localStorage.removeItem('bigDataCloud_searchData');
    localStorage.removeItem('bigDataCloud_clickTracking');
    localStorage.removeItem('userEvents');
    localStorage.removeItem('failedEvents');

    console.log('All big data cloud storage cleared');
  }
}

// Create singleton instance
const bigDataCloudService = new BigDataCloudService();

export default bigDataCloudService;
